import { useState, useEffect } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, DbPrefabDetail, StoryListItem } from '../services/adminApi'
import styles from './CharactersPage.module.css'

interface Props {
  prefabId: string
}

export default function PrefabDetailPage({ prefabId }: Props) {
  const { apiKey } = useAdmin()

  const [prefab, setPrefab] = useState<DbPrefabDetail | null>(null)
  const [allStories, setAllStories] = useState<StoryListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Field editing
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({ phrases: '', alternate: '', notFor: '' })
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Story association
  const [storyError, setStoryError] = useState('')
  const [storyUpdating, setStoryUpdating] = useState<string | null>(null)

  useEffect(() => {
    const api = createAdminApi(() => apiKey)
    setLoading(true)
    setError('')
    Promise.all([api.getDbPrefab(prefabId), api.getStories()])
      .then(([t, stories]) => {
        setPrefab(t)
        setAllStories(stories)
      })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [apiKey, prefabId])

  function startEdit() {
    if (!prefab) return
    setDraft({ phrases: prefab.phrases, alternate: prefab.alternate ?? '', notFor: prefab.notFor ?? '' })
    setSaveError('')
    setEditing(true)
  }

  async function saveFields() {
    if (!prefab) return
    const api = createAdminApi(() => apiKey)
    setSaving(true)
    setSaveError('')
    try {
      const updated = await api.updateDbPrefab(prefab.id, draft.phrases, draft.alternate, draft.notFor)
      setPrefab(updated)
      setEditing(false)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  async function toggleStory(storyId: string, checked: boolean) {
    if (!prefab) return
    const api = createAdminApi(() => apiKey)
    const currentIds = prefab.stories.map(s => s.id)
    const newIds = checked
      ? [...currentIds, storyId]
      : currentIds.filter(id => id !== storyId)
    setStoryError('')
    setStoryUpdating(storyId)
    try {
      const updated = await api.setDbPrefabStories(prefab.id, newIds)
      setPrefab(updated)
    } catch (e) {
      setStoryError(e instanceof Error ? e.message : String(e))
    } finally {
      setStoryUpdating(null)
    }
  }

  async function deletePrefab() {
    if (!prefab || !confirm(`Delete prefab "${prefab.name}"? This cannot be undone.`)) return
    const api = createAdminApi(() => apiKey)
    try {
      await api.deleteDbPrefab(prefab.id)
      window.location.hash = '#prefabs'
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const linkedIds = new Set(prefab?.stories.map(s => s.id) ?? [])
  const dbStories = allStories.filter(s => s.inDatabase)

  return (
    <div className={styles.detailPage}>
      <header className={styles.detailHeader}>
        <a href="#prefabs" className={styles.back}>← Prefabs</a>
        {prefab && <h1>{prefab.name}</h1>}
      </header>

      <div className={styles.detailContent}>
        {loading && <p className={styles.empty}>Loading…</p>}
        {error && <p className={styles.error}>{error}</p>}

        {prefab && (
          <>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>Fields</p>
                {!editing && <button className={styles.editBtn} onClick={startEdit}>Edit</button>}
              </div>

              {editing ? (
                <>
                  <label className={styles.fieldLabel}>Phrases (slash-separated)</label>
                  <input
                    className={styles.fieldInput}
                    value={draft.phrases}
                    onChange={e => setDraft(p => ({ ...p, phrases: e.target.value }))}
                    disabled={saving}
                    placeholder="hello / hey / hi"
                  />
                  <label className={styles.fieldLabel}>Alternate (shown after first fire)</label>
                  <input
                    className={styles.fieldInput}
                    value={draft.alternate}
                    onChange={e => setDraft(p => ({ ...p, alternate: e.target.value }))}
                    disabled={saving}
                    placeholder="Optional alternate text"
                  />
                  <label className={styles.fieldLabel}>Not For (phrases to suppress prefab)</label>
                  <input
                    className={styles.fieldInput}
                    value={draft.notFor}
                    onChange={e => setDraft(p => ({ ...p, notFor: e.target.value }))}
                    disabled={saving}
                    placeholder="Optional exclusion phrases"
                  />
                  {saveError && <p className={styles.saveError}>{saveError}</p>}
                  <div className={styles.editActions}>
                    <button className={styles.cancelBtn} onClick={() => setEditing(false)} disabled={saving}>
                      Cancel
                    </button>
                    <button className={styles.saveBtn} onClick={saveFields} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>ID</span>
                    <span className={styles.fieldMono}>{prefab.id}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Name</span>
                    <span className={styles.fieldValue}>{prefab.name}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Phrases</span>
                    <span className={styles.fieldValue}>{prefab.phrases || '—'}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Alternate</span>
                    <span className={styles.fieldValue}>{prefab.alternate || '—'}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Not For</span>
                    <span className={styles.fieldValue}>{prefab.notFor || '—'}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Updated</span>
                    <span className={styles.fieldValue}>{new Date(prefab.updatedAt).toLocaleString()}</span>
                  </div>
                </>
              )}
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>Stories ({linkedIds.size} linked)</p>
              </div>

              {storyError && <p className={styles.saveError}>{storyError}</p>}

              {dbStories.length === 0 ? (
                <span className={styles.noStories}>No stories in database.</span>
              ) : (
                <table className={styles.storyTable}>
                  <thead>
                    <tr>
                      <th className={styles.storyTableCheck}></th>
                      <th className={styles.storyTableTitle}>Story</th>
                      <th className={styles.storyTableFile}>Filename</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbStories.map(s => {
                      const linked = linkedIds.has(s.id)
                      const busy = storyUpdating === s.id
                      return (
                        <tr key={s.id} className={linked ? styles.storyRowLinked : styles.storyRow}>
                          <td className={styles.storyTableCheck}>
                            <input
                              type="checkbox"
                              checked={linked}
                              disabled={busy}
                              onChange={e => toggleStory(s.id, e.target.checked)}
                            />
                          </td>
                          <td className={styles.storyTableTitle}>{s.title || s.id}</td>
                          <td className={styles.storyTableFile}>{s.sourceFilename || '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className={styles.section}>
              <p className={styles.sectionTitle}>Danger Zone</p>
              <button className={styles.deleteBtn} onClick={deletePrefab}>Delete Prefab</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
